import type { ServerAreaFile } from '../ServerAreaFile.js';

export type AreaTypeRegistration = {
  areaFile: ServerAreaFile;
  gameId?: string;
  version?: number;
  /** How many instances of this type this host may run concurrently (capacity). */
  maxConcurrent?: number;
  /** Default empty-teardown grace for this type's instances (per-instance policy overrides). */
  emptyTtlMs?: number;
  /** Default room size for this type's instances (per-instance policy overrides). */
  maxClients?: number;
  /**
   * The module the area file was loaded from (the config's `areaFilePath`, as
   * written there), when it came from one. Lets tooling recompile a type's
   * source without re-importing templates to find it. Unset for an area file
   * object registered directly.
   */
  sourcePath?: string;
};

/** Options `register` accepts (everything but the area file itself). */
export type AreaTypeRegistrationOptions = Omit<AreaTypeRegistration, 'areaFile'>;

/**
 * Registry of area *types* (templates) an instance can be created from.
 * Boot-populated from the static area configs and extendable at runtime via
 * `register`. It holds templates only — no per-instance state.
 */
export class AreaTypeRegistry {
  private readonly byType: Map<string, AreaTypeRegistration> = new Map();

  public register(
    areaFile: ServerAreaFile,
    opts: AreaTypeRegistrationOptions = {},
  ): AreaTypeRegistration {
    const type = areaFile?.type;
    if (type === undefined || type === null || type === '') {
      throw new Error('Cannot register an area type without a `type`.');
    }
    const registration: AreaTypeRegistration = {
      areaFile,
      gameId: opts.gameId,
      version: opts.version,
      maxConcurrent: opts.maxConcurrent,
      emptyTtlMs: opts.emptyTtlMs,
      maxClients: opts.maxClients,
      ...(opts.sourcePath !== undefined ? { sourcePath: opts.sourcePath } : {}),
    };
    this.byType.set(type as string, registration);
    return registration;
  }

  /**
   * Swap the area file of an already-registered type (a hot reload of its
   * module), keeping every other part of the registration — capacity, defaults,
   * game, source. Instances spawned from now on run `areaFile`; live instances
   * are not touched. Throws for an unknown type (register it instead) or a
   * file whose `type` differs.
   */
  public replace(type: string, areaFile: ServerAreaFile, opts: { sourcePath?: string } = {}): AreaTypeRegistration {
    const existing = this.byType.get(type);
    if (!existing) throw new Error(`Cannot replace area type "${type}": it is not registered.`);
    if (String(areaFile?.type) !== String(type)) {
      throw new Error(`Cannot replace area type "${type}" with an area file of type "${String(areaFile?.type)}".`);
    }
    const next: AreaTypeRegistration = { ...existing, areaFile, ...(opts.sourcePath !== undefined ? { sourcePath: opts.sourcePath } : {}) };
    this.byType.set(type, next);
    return next;
  }

  /** `{ type: maxConcurrent }` for every capped type (uncapped types omitted). */
  public capacities(): { [type: string]: number } {
    const out: { [type: string]: number } = {};
    this.byType.forEach((reg, type) => {
      if (reg.maxConcurrent !== undefined) {
        out[type] = reg.maxConcurrent;
      }
    });
    return out;
  }

  /** `{ type: { emptyTtlMs?, maxClients? } }` for every type that declares a policy default. */
  public defaults(): { [type: string]: { emptyTtlMs?: number; maxClients?: number } } {
    const out: { [type: string]: { emptyTtlMs?: number; maxClients?: number } } = {};
    this.byType.forEach((reg, type) => {
      if (reg.emptyTtlMs !== undefined || reg.maxClients !== undefined) {
        out[type] = {
          ...(reg.emptyTtlMs !== undefined ? { emptyTtlMs: reg.emptyTtlMs } : {}),
          ...(reg.maxClients !== undefined ? { maxClients: reg.maxClients } : {}),
        };
      }
    });
    return out;
  }

  public has(type: string): boolean {
    return this.byType.has(type);
  }

  public get(type: string): AreaTypeRegistration | undefined {
    return this.byType.get(type);
  }

  public types(): string[] {
    return [...this.byType.keys()];
  }
}
